
import { toast } from "sonner";
import { Investor, Contact } from "@/types/investor";
import { useFileDataHandler } from "./useFileDataHandler";
import { useProcessingOperations } from "./useProcessingOperations";
import { useSaveInvestorData } from "./useSaveInvestorData";
import { loadProcessingProgress } from "@/utils/investors/investorStateUtils";
import { useCallback } from "react";

/**
 * Main hook composing all investor action capabilities
 */
export const useInvestorActions = (
  investors: Investor[],
  setInvestors: (investors: Investor[]) => void,
  contacts: Contact[],
  setContacts: (contacts: Contact[]) => void,
  selectedInvestor: Investor | null,
  setSelectedInvestor: (investor: Investor | null) => void,
  processedIds: Set<string>,
  setProcessedIds: (processedIds: Set<string>) => void,
  relevanceContext: string
) => {
  // Compose all the specialized hooks
  const { 
    saveDataToSupabaseAndLocal, 
    saveInvestorUpdate,
    startBatchProcessing,
    endBatchProcessing,
    isSyncDisabled
  } = useSaveInvestorData();
  
  const { handleFileData } = useFileDataHandler(
    setInvestors,
    setContacts,
    setProcessedIds
  );
  
  const {
    isAnalyzing,
    isProcessingBatch,
    isEnriching,
    setIsEnriching,
    handleRelevanceSubmit,
    handleProcessInvestor,
    handleEnrichInvestor,
    handleProcessBatch: handleProcessBatchInternal,
    handleInvestorUpdate,
  } = useProcessingOperations(
    investors,
    setInvestors,
    processedIds,
    setProcessedIds,
    selectedInvestor,
    setSelectedInvestor,
    contacts
  );
  
  // Enhanced batch processing with improved database save coordination
  const handleProcessBatch = useCallback(async (batchSize: number) => {
    try {
      // Disable synchronization during batch processing
      startBatchProcessing();
      
      // Process the batch with standard functionality
      const result = await handleProcessBatchInternal(batchSize);
      
      // Ensure all data is flushed to database
      await endBatchProcessing();
      
      return result;
    } catch (error) {
      console.error("Error in handleProcessBatch:", error);
      // Ensure synchronization is re-enabled even in case of error
      endBatchProcessing();
      throw error;
    }
  }, [handleProcessBatchInternal, startBatchProcessing, endBatchProcessing]);
  
  // Define handleReprocess here to avoid circular imports
  const handleReprocess = () => {
    // Get the current data from storage
    const { investors, contacts } = loadProcessingProgress();
    
    if (!investors || investors.length === 0) {
      toast.error("No data to reprocess. Please upload your Excel file first.");
      return;
    }
    
    // Reset the investors to their initial state (with contacts matched)
    const resetInvestors = investors.map(investor => ({
      ...investor,
      relevanceScore: 0,
      lastRelevanceUpdate: null,
      enrichedWith: undefined
    }));
    
    setInvestors(resetInvestors);
    setProcessedIds(new Set());
    saveDataToSupabaseAndLocal(resetInvestors, contacts, new Set());
    
    toast.success("Data reset successfully. Ready for processing.");
  };

  return {
    // AI processing states
    isAnalyzing,
    isProcessingBatch,
    isEnriching,
    setIsEnriching,
    
    // Action handlers
    handleFileData: (investors: Investor[], contacts: Contact[], shouldMerge?: boolean) => 
      handleFileData(investors, contacts, shouldMerge),
    handleRelevanceSubmit: (context: string) => handleRelevanceSubmit(context),
    handleProcessInvestor: (investor: Investor) => handleProcessInvestor(investor, relevanceContext),
    handleEnrichInvestor: (investor: Investor) => handleEnrichInvestor(investor, relevanceContext),
    handleProcessBatch,
    handleReprocess,
    handleInvestorUpdate,
    isSyncDisabled
  };
};
